import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import {
  IFtseFile,
  useGetFtseFilesQuery,
  useDeleteFtseFileMutation,
  GetFtseFilesDocument,
  IGetFtseFilesQuery
} from '../../generated/graphql';
import { DATE_TIME_FORMATS } from '../../utils/constants';
import OverviewTable, { IHeadCell } from '../Common/Tables/OverviewTable';
import { Actions } from '../../enums/ActionEnums';
import { AppContext } from '../../AppContext';
import FtseForm from './ftseForm';
import Loader from '../Common/Loader';
import FtseUpload from './FtseUpload';
import FtseDownloadForm from './FtseDownloadForm/FtseDownloadForm';
import { Box } from '@mui/material';

const FtseList: React.FunctionComponent = () => {
  const { data, loading } = useGetFtseFilesQuery();
  const [allData, setAllData] = useState<IFtseFile[]>([]);
  const { dispatch } = useContext(AppContext);
  const [deleteFtseFile] = useDeleteFtseFileMutation();
  const [openCreateFtseForm, setOpenCreateFtseForm] = useState(false);
  const [ftseFile, setFtseFile] = useState<IFtseFile | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [openUpload, setOpenUpload] = useState(false);
  const [openDownloadForm, setOpenDownloadForm] = useState(false);

  useEffect(() => {
    if (data && data.result) {
      setAllData(data.result as IFtseFile[]);
    }
  }, [data]);

  const formatDate = (rowData: IFtseFile, name: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return rowData[name]
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        moment(parseInt(rowData[name] as string)).format(DATE_TIME_FORMATS.DATE_AND_TIME)
      : '';
  };

  const remove = (rowData: IFtseFile) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.name}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (rowData: IFtseFile) => {
    if (!rowData.id) {
      return;
    }
    dispatch({ type: Actions.HideConfirmation });
    deleteFtseFile({
      variables: {
        id: rowData.id
      },
      update(cache) {
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `Ftse File deleted ${rowData.name}`, severity: 'success' }
        });

        const ftseFilesQuery = cache.readQuery({
          query: GetFtseFilesDocument
        }) as IGetFtseFilesQuery;
        if (ftseFilesQuery.result) {
          const cacheFtseFileIndex = ftseFilesQuery.result.findIndex((c) => c?.id === rowData.id);
          if (cacheFtseFileIndex > -1) {
            const data = ftseFilesQuery.result.filter(function (el) {
              return el?.id !== rowData.id;
            });
            cache.writeQuery({
              query: GetFtseFilesDocument,
              data: { result: data }
            });
          }
        }
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: { text: e.message, severity: 'error', autoHide: 6000 }
      });
    });
  };

  const handleClose = (saved: boolean, isNew?: boolean, ftseFileInput?: IFtseFile) => {
    if (saved && isNew) {
      addFtseFile(ftseFileInput as IFtseFile);
    }
    setFtseFile(null);
    setOpenCreateFtseForm(false);
  };

  const addFtseFile = (input: IFtseFile) => {
    const existing = allData.find((ftse) => ftse.id === input.id);
    if (!existing) {
      setAllData([input, ...allData]);
    }
  };

  const handleDetails = (rowData: IFtseFile) => {
    setFtseFile(rowData);
    setOpenCreateFtseForm(true);
  };

  const handleDownload = () => {
    setOpenDownloadForm(true);
  };

  const columns = [
    {
      title: 'Details',
      width: 60,
      sorting: false
    },
    {
      title: 'File Name',
      field: 'name',
      width: 106
    },
    {
      title: 'File Type',
      field: 'type.name',
      width: 136
    },
    {
      title: 'Location on Server',
      field: 'file_location',
      width: 500
    },
    {
      title: 'Last Imported',
      field: 'last_import',
      width: 185,
      type: 'date',
      render: (rowData: IFtseFile) => formatDate(rowData, 'last_import')
    },
    {
      title: 'Last Successful Get',
      field: 'last_get',
      width: 185,
      type: 'date',
      render: (rowData: IFtseFile) => formatDate(rowData, 'last_get')
    },
    {
      title: 'Attempts',
      field: 'attempt',
      width: 90
    },
    {
      title: 'Remove',
      width: 71,
      sorting: false
    }
  ];

  return (
    <Box className="overviewPage">
      <Box className="print\:w-full">
        <OverviewTable
          columns={columns as IHeadCell[]}
          data={allData}
          loading={loading}
          handleDetails={handleDetails}
          handleRemove={remove}
          pageSize={15}
          rowsPerPageOptions={[15, 25, 50, 100]}
          createButtonProps={{
            id: 'create-ftse-button',
            onClick: () => setOpenCreateFtseForm(true),
            text: 'Create FTSE',
            disabled: false
          }}
          importButtonProps={{
            id: 'import-ftse-button',
            onClick: () => setOpenUpload(true)
          }}
          downloadButtonProps={{
            id: 'download-ftse-button',
            // disabled: false,
            onClick: () => handleDownload()
          }}
        />
        {openCreateFtseForm && <FtseForm handleClose={handleClose} ftseFileInput={ftseFile} />}
        {openUpload && <FtseUpload handleClose={() => setOpenUpload(false)} />}
        {openDownloadForm && (
          <FtseDownloadForm
            handleClose={() => setOpenDownloadForm(false)}
            isLoading={(isLoading) => setDownloading(isLoading)}
          />
        )}
      </Box>
      <Loader loading={downloading} />
    </Box>
  );
};

export default FtseList;
